import * as React from "react";
import { Filter } from "lucide-react";
import { Button } from "./button";
import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export interface FilterButtonProps extends React.ComponentProps<typeof Button> {
  label?: string;
  width?: string;
  appliedCount?: number;
  appliedLabels?: string[];
}

const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  (
    {
      label = "Filter",
      width,
      className,
      appliedCount = 0,
      appliedLabels = [],
      ...props
    },
    _ref
  ) => {
    const button = (
      <Button
        variant="filter"
        className={cn("gap-[6px] relative", className)}
        style={width ? { width } : undefined}
        {...props}
      >
        <span className="inline-flex h-2 md:h-5 w-2 md:w-5 items-center justify-center">
          <Filter className="size-4 md:size-5" />
        </span>
        <span className="hidden md:inline text-b4 font-medium leading-[normal]">
          {label}
        </span>
        {appliedCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--color-high)] text-white text-[10px] font-semibold px-1 leading-none">
            {appliedCount}
          </span>
        )}
      </Button>
    );

    if (appliedCount > 0 && appliedLabels.length > 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent
            side="bottom"
            sideOffset={6}
            className="max-w-[220px] p-2"
          >
            <p className="font-medium mb-1 text-[11px]">
              Applied filters ({appliedCount})
            </p>
            <ul className="space-y-0.5">
              {appliedLabels.map((l) => (
                <li key={l} className="text-[11px] opacity-80">
                  • {l}
                </li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      );
    }

    return button;
  }
);

FilterButton.displayName = "FilterButton";

export { FilterButton };
